<template>
  <div class="page-container">
    <div
      class="page-title-container d-flex flex-column justify-content-center justify-content-md-start flex-md-row align-items-center">
      <h1 class="page-title">Viajes</h1>

      <!-- Navegador de fechas -->
      <div class="date-nav-row mx-auto mt-4 mt-md-0">
        <div class="date-nav">
          <button class="date-nav__arrow" type="button" @click="navBack">
            <i class="ri-arrow-left-s-line"></i>
          </button>
          <div class="date-nav__label-wrap">
            <button class="date-nav__label-btn" type="button" @click="openRangePicker">
              <i class="ri-calendar-2-line mr-1"></i>{{ dateNavLabel }}
            </button>
            <!-- Picker de rango de fechas -->
            <div v-if="rangePickerOpen" class="date-range-picker" @click.stop>
              <div class="drp-header">
                <button class="drp-nav" type="button" @click="prevPickerMonth">
                  <i class="ri-arrow-left-s-line"></i>
                </button>
                <span class="drp-month-label">{{ pickerMonthLabel }}</span>
                <button class="drp-nav" type="button" @click="nextPickerMonth">
                  <i class="ri-arrow-right-s-line"></i>
                </button>
              </div>
              <div class="drp-weekdays">
                <span v-for="wd in ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do']" :key="wd" class="drp-weekday">{{ wd
                }}</span>
              </div>
              <div class="drp-grid">
                <button v-for="(day, idx) in pickerGrid" :key="idx" type="button" :class="['drp-day',
                  !day ? 'drp-day--empty' : '',
                  day && isPickerStart(day) ? 'drp-day--start' : '',
                  day && isPickerEnd(day) ? 'drp-day--end' : '',
                  day && isPickerInRange(day) ? 'drp-day--range' : '',
                  day && isPickerToday(day) && !isPickerStart(day) && !isPickerEnd(day) ? 'drp-day--today' : ''
                ]" @click="day && onPickerDayClick(day)">
                  <span v-if="day">{{ day.getDate() }}</span>
                </button>
              </div>
              <div class="drp-inputs">
                <div class="drp-input-group">
                  <label class="drp-input-label">Desde</label>
                  <input type="date" class="form-control form-control-sm" :value="pickerInputDesde"
                    @input="onPickerDesdeInput($event.target.value)" />
                </div>
                <div class="drp-input-sep">—</div>
                <div class="drp-input-group">
                  <label class="drp-input-label">Hasta</label>
                  <input type="date" class="form-control form-control-sm" :value="pickerInputHasta"
                    @input="onPickerHastaInput($event.target.value)" />
                </div>
              </div>
              <div class="drp-footer">
                <button type="button" class="button button-light" @click="closeRangePicker">Cancelar</button>
                <button type="button" class="button button-primary" @click="applyRangePicker">Aplicar</button>
              </div>
            </div>
          </div>
          <button class="date-nav__arrow" type="button" @click="navForward">
            <i class="ri-arrow-right-s-line"></i>
          </button>
          <div class="date-nav__sep"></div>
          <div class="date-nav__chips">
            <button :class="['date-nav__chip', { 'is-active': activeChip === 'today' }]" type="button"
              @click="setDateChip('today')">Hoy</button>
            <button :class="['date-nav__chip', { 'is-active': activeChip === 'yesterday' }]" type="button"
              @click="setDateChip('yesterday')">Ayer</button>
            <button :class="['date-nav__chip', { 'is-active': activeChip === 'week' }]" type="button"
              @click="setDateChip('week')">Esta semana</button>
            <button :class="['date-nav__chip', { 'is-active': activeChip === 'month' }]" type="button"
              @click="setDateChip('month')">Este mes</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Cards de estadísticas -->
    <div class="stats-row">
      <div :class="['stat-card', 'stat-card--total', { 'is-active': activeCard === 'total' }]"
        @click="setCardFilter('total', null, null)">
        <div class="stat-card__label">TOTAL</div>
        <div class="stat-card__value">{{ stats.total || 0 }}</div>
        <div class="stat-card__sub">todos los registros</div>
      </div>
      <div :class="['stat-card', 'stat-card--pendiente', { 'is-active': activeCard === 'pendiente' }]"
        @click="setCardFilter('pendiente', 'estado', 'PENDIENTE')">
        <div class="stat-card__label"><span class="stat-card__dot"></span>PENDIENTE</div>
        <div class="stat-card__value">{{ stats.pendiente || 0 }}</div>
        <div class="stat-card__sub">Sin asignar</div>
      </div>
      <div :class="['stat-card', 'stat-card--asignado', { 'is-active': activeCard === 'asignado' }]"
        @click="setCardFilter('asignado', 'estado', 'ASIGNADO')">
        <div class="stat-card__label"><span class="stat-card__dot"></span>ASIGNADO</div>
        <div class="stat-card__value">{{ stats.asignado || 0 }}</div>
        <div class="stat-card__sub">Con chofer</div>
      </div>
      <div :class="['stat-card', 'stat-card--ubicado', { 'is-active': activeCard === 'ubicado' }]"
        @click="setCardFilter('ubicado', 'estado', 'UBICADO')">
        <div class="stat-card__label"><span class="stat-card__dot"></span>UBICADO</div>
        <div class="stat-card__value">{{ stats.ubicado || 0 }}</div>
        <div class="stat-card__sub">En punto</div>
      </div>
      <div :class="['stat-card', 'stat-card--finalizado', { 'is-active': activeCard === 'finalizado' }]"
        @click="setCardFilter('finalizado', 'estado', 'FINALIZADO')">
        <div class="stat-card__label"><span class="stat-card__dot"></span>FINALIZADO</div>
        <div class="stat-card__value">{{ stats.finalizado || 0 }}</div>
        <div class="stat-card__sub">Completados</div>
      </div>
      <div :class="['stat-card', 'stat-card--cancelado', { 'is-active': activeCard === 'cancelado' }]"
        @click="setCardFilter('cancelado', 'estado', 'CANCELADO')">
        <div class="stat-card__label"><span class="stat-card__dot"></span>CANCELADO</div>
        <div class="stat-card__value">{{ stats.cancelado || 0 }}</div>
        <div class="stat-card__sub">Cancelados</div>
      </div>
      <div :class="['stat-card', 'stat-card--enproceso', { 'is-active': activeCard === 'enproceso' }]"
        @click="setCardFilter('enproceso', 'estado', 'EN_PROCESO')">
        <div class="stat-card__label"><span class="stat-card__dot"></span>EN PROCESO</div>
        <div class="stat-card__value">{{ stats.en_proceso || 0 }}</div>
        <div class="stat-card__sub">En servicio</div>
      </div>
      <div :class="['stat-card', 'stat-card--nonshow', { 'is-active': activeCard === 'nonshow' }]"
        @click="setCardFilter('nonshow', 'estado', 'NON_SHOW')">
        <div class="stat-card__label"><span class="stat-card__dot"></span>NON SHOW</div>
        <div class="stat-card__value">{{ stats.non_show || 0 }}</div>
        <div class="stat-card__sub">No presentó</div>
      </div>
      <div :class="['stat-card', 'stat-card--error', { 'is-active': activeCard === 'error' }]"
        @click="setCardFilter('error', 'estado', 'ERROR')">
        <div class="stat-card__label"><span class="stat-card__dot"></span>ERROR</div>
        <div class="stat-card__value">{{ stats.error || 0 }}</div>
        <div class="stat-card__sub">Con error</div>
      </div>
      <div :class="['stat-card', 'stat-card--pendcobro', { 'is-active': activeCard === 'pendcobro' }]"
        @click="setCardFilter('pendcobro', 'estado_pago', 'Pendiente')">
        <div class="stat-card__label"><span class="stat-card__icon">⚠</span>PEND. COBRO</div>
        <div class="stat-card__value">{{ stats.pago_pendiente || 0 }}</div>
        <div class="stat-card__sub">viajes sin pagar</div>
      </div>
    </div>

    <div class="table-container">
      <div class="table-header">

        <!-- Fila: selects inline + recarga | buscador + agregar -->
        <div class="toolbar-row">
          <div class="toolbar-filters">
            <select v-model="inlineFilters.fuente" class="toolbar-select" @change="onFilterChange">
              <option value="">Todas las fuentes</option>
              <option v-for="s in allSourceOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
            </select>
            <select v-model="inlineFilters.estado_pago" class="toolbar-select" @change="onFilterChange">
              <option value="">Estado de pago</option>
              <option value="Pendiente">Pendiente</option>
              <option value="Pagado">Pagado</option>
              <option value="Cancelado">Cancelado</option>
            </select>
            <select v-model="inlineFilters.moneda" class="toolbar-select" @change="onFilterChange">
              <option value="">Moneda</option>
              <option value="SOLES">SOLES</option>
              <option value="DOLARES">DOLARES</option>
            </select>
            <select v-model="inlineFilters.tipo_viaje" class="toolbar-select" @change="onFilterChange">
              <option value="">Tipo de viaje</option>
              <option value="APTO-HOT">APTO-HOT</option>
              <option value="HOT-APTO">HOT-APTO</option>
              <option value="DISTRITAL">DISTRITAL</option>
            </select>
            <button v-if="hasActiveFilters" type="button" class="toolbar-clear-icon" title="Limpiar filtros"
              @click="clearInlineFilters">
              <i class="ri-close-line"></i>
            </button>
          </div>
          <div class="toolbar-right">
            <button type="button" class="button-tooltip text-dark font-weight-bold mr-0" v-b-tooltip.hover title="Recargar"
              @click="loadData()">
              <i class="ri-loop-right-line"></i>
            </button>
            <button type="button" class="button-tooltip text-dark font-weight-bold" v-b-tooltip.hover
              title="Descargar" @click="exportExcel()">
              <i class="ri-download-2-line"></i>
            </button>
            <SearchBar placeholder="Buscar por ride, pasajero, celular, ruta..." @search="onSearch"
              @input="search = $event" />
            <!-- <button type="button" class="button button-light btn-export" :disabled="exporting" @click="exportExcel">
              <i class="ri-file-excel-2-line"></i> {{ exporting ? 'Exportando...' : 'Excel' }}
            </button> -->
            <b-button :to="{ name: 'viajes-ride-create' }" variant="none"
              class="button button-primary">Agregar</b-button>
          </div>
        </div>

      </div><!-- /table-header -->

      <div class="mb-0 table-scroll" :style="list.length ? 'min-height: 300px' : ''">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="col-ride">Ride</th>
              <th class="col-fecha">Fecha / Hora</th>
              <th class="col-fuente">Fuente</th>
              <th class="col-ruta">Ruta</th>
              <th class="col-tipo">Tipo / Vuelo</th>
              <th class="col-pasajero">Pasajero</th>
              <th class="col-pax text-center">Pax / Mal.</th>
              <th class="col-vehiculo text-center">Vehículo</th>
              <th class="col-conductor">Conductor</th>
              <th class="col-estado text-center">Estado</th>
              <th class="col-pago text-center">Pago / Medio</th>
              <th class="col-precio text-center">Precio</th>
              <th class="col-opciones pr-md-4">Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in list" :key="item.id" class="clickable-row" @click.stop="openDrawer(item.id)">
              <td class="text-center font-weight-bold ride-code">{{ item.ride }}</td>

              <td class="text-center">
                <div>{{ item.fecha }}</div>
                <div class="text-muted small">{{ formatHora(item.hora) }}</div>
              </td>

              <td class="text-center">
                <span :class="`badge badge-fuente badge-fuente--${normalizeFuente(item.fuente)}`">{{ item.fuente
                }}</span>
              </td>


              <td class="d-flex flex-column">
                <div class="ruta-item">
                  <span class="ruta-dot ruta-dot--origen"></span>
                  <span class="ruta-text">{{ item.origen }}</span>
                </div>
                <div class="ruta-item mt-1">
                  <span class="ruta-dot ruta-dot--destino"></span>
                  <span class="ruta-text">{{ item.destino }}</span>
                </div>
              </td>

              <td>
                <div class="d-flex flex-column jsutify-content-center align-items-center">
                  <div>{{ item.tipo_viaje }}</div>
                  <div v-if="item.num_vuelo" class="text-muted small">{{ item.num_vuelo }}</div>
                </div>
              </td>

              <td>
                <div>{{ item.nombres }} {{ item.apellidos }}</div>
                <div v-if="item.registrado_por" class="text-muted small">Reg. por: {{ item.registrado_por }}</div>
              </td>

              <td class="text-center">
                <div class="pax-info">
                  <span><i class="ri-user-line"></i> {{ item.cant_pax }}</span>
                  <span class="ml-2"><i class="ri-briefcase-line"></i> {{ item.cant_maletas }}</span>
                </div>
              </td>

              <td class="text-center">
                <span class="badge badge-vehicle">{{ item.tipo_vehiculo }}</span>
              </td>

              <td class="d-flex flex-column" @click.stop>
                <div v-if="item.conductor_nombre" class="conductor-cell conductor-cell--clickable"
                  @click="openAssignModal(item)">
                  <div class="conductor-avatar" :style="{ backgroundColor: getDriverColor(item.conductor_nombre) }">
                    {{ getDriverInitials(item.conductor_nombre) }}
                  </div>
                  <div class="conductor-info">
                    <span class="conductor-info__name">{{ item.conductor_nombre }}</span>
                    <span v-if="item.vehiculo_placa" class="conductor-info__plate">{{ item.vehiculo_placa }}</span>
                  </div>
                </div>
                <button v-else class="btn-asignar" type="button" @click="openAssignModal(item)">
                  <i class="ri-user-add-line"></i> Asignar
                </button>
              </td>

              <td class="text-center" @click.stop>
                <span
                  :class="[`badge ${getEstadoColor(item.estado)}`, isTerminalEstado(item.estado) ? '' : 'badge-estado-clickable']"
                  @click="openStatusDropdown(item, $event)">{{ statusMeta[item.estado] ? statusMeta[item.estado].label :
                  item.estado }}</span>
              </td>

              <td class="text-center" @click.stop>
                <span :class="`badge badge-pago-clickable ${getPagoColor(item.estado_pago)}`"
                  @click="openPaymentModal(item)">{{ item.estado_pago }}</span>
                <div v-if="item.medio_pago" class="text-muted small mt-1">{{ item.medio_pago }}</div>
              </td>

              <td class="text-center">
                <template v-if="item.descuento_aplicado && parseFloat(item.descuento_aplicado) > 0">
                  <span class="price-original">{{ getMonedaLabel(item.moneda) }} {{ formatPrecio(item.precio) }}</span>
                  <br />
                  <span class="font-weight-bold text-success">{{ getMonedaLabel(item.moneda) }} {{
                    formatPrecio(item.precio_final || item.precio) }}</span>
                </template>
                <span v-else class="font-weight-bold">{{ getMonedaLabel(item.moneda) }} {{ formatPrecio(item.precio)
                  }}</span>
              </td>

              <td class="text-center d_option pr-md-3" @click.stop>
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem text="Ver detalle" @click="openDrawer(item.id)" />
                    <DropdownItem text="Editar" @click="onEditar(item)" />
                    <DropdownItem text="Registrar pago" @click="openPaymentModal(item)" />
                    <DropdownItem text="Eliminar" @click="onEliminar(item)" />
                  </template>
                </DropdownTable>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer d-flex justify-content-end align-items-center">
        <pagination :total="total" :page="page" :page-size="pageSize" :pages="pages" :changePage="changePage"
          :changePageSize="changePageSize" v-if="list.length" />

        <div class="table-no-results" v-else>
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </div>
    </div>

    <!-- Modal confirmación eliminar -->
    <ConfirmationModal :show="confirmationModal.show" @hide="closeConfirmationModal" :data="confirmationModal"
      @confirm="confirmModal" />

    <!-- Modal asignación conductor/vehículo -->
    <b-modal v-model="assignModal.show" title="Asignar conductor y vehículo" hide-footer centered
      @hide="closeAssignModal">
      <div class="form-group">
        <label class="assign-label">Conductor</label>
        <select v-model="assignModal.conductor_id" class="form-control">
          <option :value="null">— Sin conductor —</option>
          <option v-for="d in driverOptions" :key="d.id" :value="d.id">
            {{ d.full_name }}
          </option>
        </select>
      </div>
      <div class="form-group mt-3">
        <label class="assign-label">Vehículo</label>
        <select v-model="assignModal.vehiculo_id" class="form-control">
          <option :value="null">— Sin vehículo —</option>
          <option v-for="v in vehicleOptions" :key="v.id" :value="v.id">{{ v.license_plate }}</option>
        </select>
      </div>
      <div class="d-flex align-items-center mt-4">
        <button v-if="assignModal.ride && assignModal.ride.conductor_nombre" type="button" class="button button-danger"
          @click="clearAssign">
          <i class="ri-user-unfollow-line"></i> Quitar asignación
        </button>
        <div class="ml-auto d-flex">
          <button type="button" class="button button-light mr-2" @click="closeAssignModal">
            Cancelar
          </button>
          <button type="button" class="button button-primary" @click="submitAssign">
            <i class="ri-user-follow-line"></i> Asignar
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Modal registrar pago -->
    <b-modal v-model="paymentModal.show" title="Registrar pago" hide-footer centered size="md"
      @hide="closePaymentModal">
      <div class="form-row">
        <div class="form-group col-md-6">
          <label class="assign-label">Estado de pago <span class="text-danger">*</span></label>
          <select v-model="paymentModal.estado_pago" class="form-control">
            <option value="">— Seleccionar —</option>
            <option value="Pendiente">Pendiente</option>
            <option value="Pagado">Pagado</option>
            <option value="Cancelado">Cancelado</option>
          </select>
        </div>
        <div class="form-group col-md-6">
          <label class="assign-label">Medio de pago</label>
          <select v-model="paymentModal.medio_pago" class="form-control">
            <option value="">— Sin medio —</option>
            <option value="ELIFE">ELIFE</option>
            <option value="POLITTAN">POLITTAN</option>
            <option value="VIATOR">VIATOR</option>
            <option value="LINK DE PAGO">LINK DE PAGO</option>
            <option value="TRANSFERENCIA">TRANSFERENCIA</option>
            <option value="YAPE">YAPE</option>
            <option value="PAGO POS">PAGO POS</option>
            <option value="EFECTIVO">EFECTIVO</option>
            <option value="CORPORATIVO">CORPORATIVO</option>
          </select>
        </div>
      </div>
      <div class="form-row">
        <div class="form-group col-md-6">
          <label class="assign-label">Fecha de pago</label>
          <input type="date" v-model="paymentModal.fecha_pago" class="form-control" />
        </div>
        <div class="form-group col-md-6">
          <label class="assign-label">Hora de pago</label>
          <input type="time" v-model="paymentModal.hora_pago" class="form-control" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-group col-md-6">
          <label class="assign-label">N° Operación / Transacción</label>
          <input type="text" v-model="paymentModal.num_operacion" class="form-control" placeholder="Ej: 123456789" />
        </div>
        <div class="form-group col-md-6">
          <label class="assign-label">Comprobante</label>
          <input type="text" v-model="paymentModal.comprobante" class="form-control" placeholder="Ej: F001-00123" />
        </div>
      </div>
      <div class="form-group">
        <label class="assign-label">Responsable del pago</label>
        <input type="text" :value="currentUserName" class="form-control" readonly style="background-color: #f9fafb;" />
      </div>
      <div class="d-flex justify-content-end mt-3">
        <button type="button" class="button button-light mr-2" @click="closePaymentModal">
          Cancelar
        </button>
        <button type="button" class="button button-primary" @click="submitPayment">
          <i class="ri-save-line"></i> Guardar pago
        </button>
      </div>
    </b-modal>

    <!-- Dropdown de cambio de estado -->
    <div v-if="statusDropdown.show" class="status-dropdown" @click.stop
      :style="{ top: statusDropdown.top + 'px', left: statusDropdown.left + 'px' }">
      <div class="status-dropdown__header">Cambiar estado</div>
      <div v-if="statusDropdown.options.length === 0" class="status-dropdown__empty">
        <i class="ri-lock-line"></i> Estado final
      </div>
      <button v-for="opt in statusDropdown.options" :key="opt.key" class="status-dropdown__item" type="button"
        @click="submitStatus(opt.key)">
        <span :class="`badge ${opt.badge} mr-2`">{{ opt.label }}</span>
      </button>
    </div>

    <!-- OVERLAY -->
    <transition name="fade">
      <div v-if="drawerOpen" class="ride-overlay" @click="closeDrawer"></div>
    </transition>

    <!-- DRAWER -->
    <transition name="slide-drawer">
      <div v-if="drawerOpen" class="ride-drawer">

        <div class="drawer-header">
          <div class="drawer-header__main">
            <span class="drawer-ride-code">{{ drawerRide.ride }}</span>
            <div class="drawer-ride-datetime">
              <i class="ri-calendar-line"></i> {{ drawerRide.fecha }}
              &nbsp;&nbsp;
              <i class="ri-time-line"></i> {{ formatHora(drawerRide.hora) }}
            </div>
          </div>
          <button class="drawer-close" @click="closeDrawer">
            <i class="ri-close-line"></i>
          </button>
        </div>

        <div class="drawer-body">

          <div class="drawer-section">
            <div class="drawer-section__title">Datos del viaje</div>
            <div class="drawer-row">
              <span class="drawer-label">Tipo fuente</span>
              <span class="drawer-value">{{ sourceTypeLabel(drawerRide.source_type) }}</span>
            </div>
            <div class="drawer-row">
              <span class="drawer-label">Fuente</span>
              <span :class="`badge badge-fuente badge-fuente--${normalizeFuente(drawerRide.fuente)}`">{{
                drawerRide.fuente
              }}</span>
            </div>
            <div v-if="drawerRide.channel" class="drawer-row">
              <span class="drawer-label">Canal</span>
              <span class="drawer-value">{{ channelLabel(drawerRide.channel) }}</span>
            </div>
            <div class="drawer-row">
              <span class="drawer-label">Tipo de viaje</span>
              <span class="drawer-value">{{ drawerRide.tipo_viaje }}</span>
            </div>
            <div v-if="drawerRide.tipo_vuelo" class="drawer-row">
              <span class="drawer-label">Tipo de vuelo</span>
              <span class="drawer-value">{{ drawerRide.tipo_vuelo }}</span>
            </div>
            <div v-if="drawerRide.num_vuelo" class="drawer-row">
              <span class="drawer-label">N° de vuelo</span>
              <span class="drawer-value">{{ drawerRide.num_vuelo }}</span>
            </div>
            <div v-if="drawerRide.observaciones" class="drawer-row">
              <span class="drawer-label">Observaciones</span>
              <span class="drawer-value">{{ drawerRide.observaciones }}</span>
            </div>
            <div v-if="drawerRide.motivo_non_show" class="drawer-row">
              <span class="drawer-label">Motivo Non Show</span>
              <span class="drawer-value">{{ drawerRide.motivo_non_show }}</span>
            </div>
            <template v-if="drawerRide.buffer_enabled">
              <div class="drawer-row">
                <span class="drawer-label">Buffer time</span>
                <span class="drawer-value">{{ drawerRide.buffer_minutes }} minutos</span>
              </div>
              <div class="drawer-row">
                <span class="drawer-label">Hora est. de recogida</span>
                <span class="drawer-value">{{ calcPickupTime(drawerRide.hora, drawerRide.buffer_minutes) }}</span>
              </div>
            </template>
          </div>

          <div class="drawer-section">
            <div class="drawer-section__title">Ruta</div>
            <div class="drawer-timeline">
              <div class="drawer-timeline__row">
                <div class="drawer-timeline__dot drawer-timeline__dot--origen"></div>
                <div class="drawer-timeline__line"></div>
                <div class="drawer-timeline__dot drawer-timeline__dot--destino"></div>
              </div>
              <div class="drawer-timeline__labels">
                <div class="drawer-timeline__label">{{ drawerRide.origen }}</div>
                <div class="drawer-timeline__label">{{ drawerRide.destino }}</div>
              </div>
            </div>
          </div>

          <div class="drawer-section">
            <div class="drawer-section__title">Pasajero</div>
            <div class="drawer-row">
              <span class="drawer-label">Nombre</span>
              <span class="drawer-value">{{ drawerRide.nombres }} {{ drawerRide.apellidos }}</span>
            </div>
            <div class="drawer-row">
              <span class="drawer-label">Celular</span>
              <span class="drawer-value">{{ drawerRide.celular }}</span>
            </div>
            <div class="drawer-row">
              <span class="drawer-label">Pax / Maletas</span>
              <span class="drawer-value">
                <i class="ri-user-line"></i> {{ drawerRide.cant_pax }}
                &nbsp;
                <i class="ri-briefcase-line"></i> {{ drawerRide.cant_maletas }}
              </span>
            </div>
          </div>

          <div class="drawer-section">
            <div class="drawer-section__title">Servicio</div>
            <div class="drawer-row">
              <span class="drawer-label">Tipo de vehículo</span>
              <span class="badge badge-vehicle">{{ drawerRide.tipo_vehiculo }}</span>
            </div>
            <div class="drawer-row">
              <span class="drawer-label">Conductor</span>
              <span v-if="drawerRide.conductor_nombre" class="drawer-value">{{ drawerRide.conductor_nombre }}</span>
              <span v-else class="text-muted">Sin asignar</span>
            </div>
            <div class="drawer-row">
              <span class="drawer-label">Vehículo</span>
              <span v-if="drawerRide.vehiculo_placa" class="drawer-value">
                {{ drawerRide.vehiculo_placa }}
                <span v-if="drawerRide.vehiculo_marca" class="text-muted"> — {{ drawerRide.vehiculo_marca }}</span>
              </span>
              <span v-else class="text-muted">Sin asignar</span>
            </div>
          </div>

          <div class="drawer-section">
            <div class="drawer-section__title">Precio y pago</div>
            <div class="drawer-row">
              <span class="drawer-label">Precio</span>
              <span v-if="drawerRide.descuento_aplicado && parseFloat(drawerRide.descuento_aplicado) > 0"
                class="drawer-value">
                <span class="price-original">{{ getMonedaLabel(drawerRide.moneda) }} {{ formatPrecio(drawerRide.precio)
                  }}</span>
                <span class="font-weight-bold text-success ml-2">{{ getMonedaLabel(drawerRide.moneda) }} {{
                  formatPrecio(drawerRide.precio_final || drawerRide.precio) }}</span>
              </span>
              <span v-else class="drawer-value font-weight-bold">{{ getMonedaLabel(drawerRide.moneda) }} {{
                formatPrecio(drawerRide.precio) }}</span>
            </div>
            <div v-if="drawerRide.descuento_aplicado && parseFloat(drawerRide.descuento_aplicado) > 0"
              class="drawer-row">
              <span class="drawer-label">Descuento</span>
              <span class="drawer-value text-danger">-{{ getMonedaLabel(drawerRide.moneda) }} {{
                formatPrecio(drawerRide.descuento_aplicado) }}</span>
            </div>
            <div class="drawer-row">
              <span class="drawer-label">Estado de pago</span>
              <span :class="`badge ${getPagoColor(drawerRide.estado_pago)}`">{{ drawerRide.estado_pago }}</span>
            </div>
            <div v-if="drawerRide.medio_pago" class="drawer-row">
              <span class="drawer-label">Medio de pago</span>
              <span class="drawer-value">{{ drawerRide.medio_pago }}</span>
            </div>
            <div v-if="drawerRide.fecha_pago" class="drawer-row">
              <span class="drawer-label">Fecha de pago</span>
              <span class="drawer-value">{{ drawerRide.fecha_pago }} {{ drawerRide.hora_pago ?
                formatHora(drawerRide.hora_pago) : '' }}</span>
            </div>
            <div v-if="drawerRide.num_operacion" class="drawer-row">
              <span class="drawer-label">N° operación</span>
              <span class="drawer-value">{{ drawerRide.num_operacion }}</span>
            </div>
            <div v-if="drawerRide.comprobante" class="drawer-row">
              <span class="drawer-label">Comprobante</span>
              <span class="drawer-value">{{ drawerRide.comprobante }}</span>
            </div>
            <div v-if="drawerRide.responsable_pago" class="drawer-row">
              <span class="drawer-label">Responsable</span>
              <span class="drawer-value">{{ drawerRide.responsable_pago }}</span>
            </div>
          </div>

        </div><!-- /drawer-body -->

        <div class="drawer-footer">
          <router-link :to="{ name: 'viajes-ride-edit', params: { id: drawerRide.id } }" class="button button-primary">
            <i class="ri-edit-line"></i> Editar viaje
          </router-link>
          <button class="button button-light ml-2" @click="closeDrawer">Cerrar</button>
        </div>

      </div>
    </transition>

  </div>
</template>

<script>
import { RideService, DriverService, VehicleService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'
import {
  Alert,
  Constants,
  // Util,
  PaginationMixin,
  DropdownTable,
  DropdownItem,
  SearchBar,
  // FilterMenu,
  FilterMixin,
  ConfirmationModal,
  ConfirmationModalMixin
} from 'wize-admin'

export default {
  components: { SearchBar, DropdownTable, DropdownItem, ConfirmationModal },
  mixins: [PaginationMixin, FilterMixin, ConfirmationModalMixin],
  data() {
    return {
      list: [],
      stats: {},
      activeCard: 'total',
      cardFilter: { field: null, value: null },
      dateMode: 'day',
      activeChip: 'today',
      navDate: new Date(),
      rangePickerOpen: false,
      pickerViewDate: new Date(),
      pickerSelStart: null,
      pickerSelEnd: null,
      rangeStart: null,
      rangeEnd: null,
      pickerInputDesde: '',
      pickerInputHasta: '',
      drawerOpen: false,
      drawerRide: {},
      assignModal: {
        show: false,
        ride: null,
        conductor_id: null,
        vehiculo_id: null
      },
      driverOptions: [],
      vehicleOptions: [],
      paymentModal: {
        show: false,
        ride: null,
        estado_pago: '',
        medio_pago: '',
        fecha_pago: '',
        hora_pago: '',
        num_operacion: '',
        comprobante: ''
      },
      statusDropdown: {
        show: false,
        ride: null,
        options: [],
        top: 0,
        left: 0
      },
      statusTransitions: {
        PENDIENTE:    ['ASIGNADO', 'CANCELADO', 'ERROR', 'REPROGRAMADO'],
        ASIGNADO:     ['EN_CAMINO', 'UBICADO', 'CANCELADO', 'PENDIENTE', 'ERROR', 'REPROGRAMADO'],
        UBICADO:      ['EN_CAMINO', 'EN_PROCESO', 'CANCELADO', 'NON_SHOW', 'ERROR'],
        EN_CAMINO:    ['UBICADO', 'EN_PROCESO', 'FINALIZADO', 'CANCELADO', 'ERROR'],
        EN_PROCESO:   ['FINALIZADO', 'CANCELADO', 'ERROR'],
        FINALIZADO:   [],
        CANCELADO:    ['PENDIENTE', 'REPROGRAMADO'],
        NON_SHOW:     ['PENDIENTE', 'REPROGRAMADO'],
        ERROR:        ['PENDIENTE', 'REPROGRAMADO'],
        REPROGRAMADO: ['PENDIENTE', 'ASIGNADO']
      },
      statusMeta: {
        PENDIENTE: { label: 'Pendiente', badge: 'badge-secondary' },
        ASIGNADO: { label: 'Asignado', badge: 'badge-info' },
        UBICADO: { label: 'Ubicado', badge: 'badge-primary' },
        EN_CAMINO: { label: 'En camino', badge: 'badge-primary' },
        EN_PROCESO: { label: 'En proceso', badge: 'badge-warning' },
        FINALIZADO: { label: 'Finalizado', badge: 'badge-success' },
        CANCELADO: { label: 'Cancelado', badge: 'badge-danger' },
        NON_SHOW: { label: 'No Show', badge: 'badge-dark' },
        ERROR: { label: 'Error', badge: 'badge-purple' },
        REPROGRAMADO: { label: 'Reprog.', badge: 'badge-secondary' }
      },
      filterOptions: [
        {
          label: "Fuente",
          key: "fuente",
          type: Constants.TypeFilters.SINGLE,
          options: []
        },
        {
          label: "Estado de pago",
          key: "estado_pago",
          type: Constants.TypeFilters.SINGLE,
          options: [
            LocalConstants.RideEstadoPago.PENDIENTE,
            LocalConstants.RideEstadoPago.PAGADO,
            LocalConstants.RideEstadoPago.CANCELADO,
          ]
        },
        {
          label: "Moneda",
          key: "moneda",
          type: Constants.TypeFilters.SINGLE,
          options: [
            LocalConstants.RideMoneda.SOLES,
            LocalConstants.RideMoneda.DOLARES,
          ]
        },
        {
          label: "Tipo de viaje",
          key: "tipo_viaje",
          type: Constants.TypeFilters.SINGLE,
          options: [
            LocalConstants.RideTipoViaje.APTO_HOT,
            LocalConstants.RideTipoViaje.HOT_APTO,
            LocalConstants.RideTipoViaje.DISTRITAL,
          ]
        }
      ],
      inlineFilters: {
        fuente: '',
        estado_pago: '',
        moneda: '',
        tipo_viaje: ''
      },
      bookingSources: { fixed: [], otas: [], affiliates: [] },
      exporting: false
    }
  },
  computed: {
    allSourceOptions() {
      const s = this.bookingSources
      return [
        ...s.fixed.map(f => ({ value: f.value, label: f.label })),
        ...s.otas.map(o => ({ value: o.value, label: o.label })),
        ...s.affiliates.map(a => ({ value: a.value, label: a.label }))
      ]
    },
    hasActiveFilters() {
      return Object.values(this.inlineFilters).some(v => v !== '')
    },
    currentUserName() {
      const user = this.$store.getters['auth/getUser']
      return user ? user.name || `${user.first_name} ${user.last_name}` : ''
    },
    pickerMonthLabel() {
      return this.formatMonthYear(this.pickerViewDate)
    },
    pickerGrid() {
      const year = this.pickerViewDate.getFullYear()
      const month = this.pickerViewDate.getMonth()
      const firstDay = new Date(year, month, 1)
      const lastDay = new Date(year, month + 1, 0)
      let startDow = firstDay.getDay()
      startDow = startDow === 0 ? 6 : startDow - 1 // Mon=0 … Sun=6
      const days = []
      for (let i = 0; i < startDow; i++) days.push(null)
      for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d))
      while (days.length % 7 !== 0) days.push(null)
      return days
    },
    dateNavLabel() {
      if (this.dateMode === 'range') {
        const desde = this.rangeStart ? this.formatDay(this.rangeStart) : '...'
        const hasta = this.rangeEnd ? this.formatDay(this.rangeEnd) : '...'
        return `${desde} — ${hasta}`
      }
      if (this.dateMode === 'month') {
        return this.formatMonthYear(this.navDate)
      }
      if (this.dateMode === 'week') {
        const monday = this.getWeekMonday(this.navDate)
        const sunday = new Date(monday)
        sunday.setDate(monday.getDate() + 6)
        return `${this.formatDay(monday)} — ${this.formatDay(sunday)}`
      }
      const label = this.formatDay(this.navDate)
      const today = new Date(); today.setHours(0, 0, 0, 0)
      const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1)
      const nav = new Date(this.navDate); nav.setHours(0, 0, 0, 0)
      if (nav.getTime() === today.getTime()) return `Hoy — ${label}`
      if (nav.getTime() === yesterday.getTime()) return `Ayer — ${label}`
      return label
    }
  },
  methods: {
    // ── Navegador de fechas ──────────────────────────────────────────────────
    toYMD(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    formatDay(date) {
      const months = ['ene.', 'feb.', 'mar.', 'abr.', 'may.', 'jun.', 'jul.', 'ago.', 'sep.', 'oct.', 'nov.', 'dic.']
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
    },
    formatMonthYear(date) {
      const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
      return `${months[date.getMonth()]} ${date.getFullYear()}`
    },
    getWeekMonday(date) {
      const d = new Date(date)
      const day = d.getDay()
      const diff = day === 0 ? -6 : 1 - day
      d.setDate(d.getDate() + diff)
      d.setHours(0, 0, 0, 0)
      return d
    },
    getDateRange() {
      if (this.dateMode === 'range') {
        return {
          desde: this.rangeStart ? this.toYMD(this.rangeStart) : '',
          hasta: this.rangeEnd ? this.toYMD(this.rangeEnd) : ''
        }
      }
      if (this.dateMode === 'week') {
        const monday = this.getWeekMonday(this.navDate)
        const sunday = new Date(monday)
        sunday.setDate(monday.getDate() + 6)
        return { desde: this.toYMD(monday), hasta: this.toYMD(sunday) }
      }
      if (this.dateMode === 'month') {
        const first = new Date(this.navDate.getFullYear(), this.navDate.getMonth(), 1)
        const last = new Date(this.navDate.getFullYear(), this.navDate.getMonth() + 1, 0)
        return { desde: this.toYMD(first), hasta: this.toYMD(last) }
      }
      const s = this.toYMD(this.navDate)
      return { desde: s, hasta: s }
    },
    resolveChip(date) {
      const today = new Date(); today.setHours(0, 0, 0, 0)
      const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1)
      const d = new Date(date); d.setHours(0, 0, 0, 0)
      if (d.getTime() === today.getTime()) return 'today'
      if (d.getTime() === yesterday.getTime()) return 'yesterday'
      return null
    },
    // ── Picker de rango ──────────────────────────────────────────────────────
    parseYMD(str) {
      if (!str || !/^\d{4}-\d{2}-\d{2}$/.test(str)) return null
      const [y, m, d] = str.split('-').map(Number)
      return new Date(y, m - 1, d)
    },
    openRangePicker() {
      this.pickerViewDate = new Date(this.navDate)
      if (this.dateMode === 'range' && this.rangeStart && this.rangeEnd) {
        this.pickerSelStart = new Date(this.rangeStart)
        this.pickerSelEnd = new Date(this.rangeEnd)
        this.pickerInputDesde = this.toYMD(this.rangeStart)
        this.pickerInputHasta = this.toYMD(this.rangeEnd)
      } else {
        const { desde, hasta } = this.getDateRange()
        this.pickerSelStart = this.parseYMD(desde)
        this.pickerSelEnd = desde !== hasta ? this.parseYMD(hasta) : null
        this.pickerInputDesde = desde
        this.pickerInputHasta = desde !== hasta ? hasta : ''
      }
      this.rangePickerOpen = true
      setTimeout(() => document.addEventListener('click', this._rangePickerHandler), 0)
    },
    closeRangePicker() {
      this.rangePickerOpen = false
      document.removeEventListener('click', this._rangePickerHandler)
    },
    applyRangePicker() {
      let start = this.pickerSelStart
      let end = this.pickerSelEnd || this.pickerSelStart
      if (!start) { this.closeRangePicker(); return }
      if (end < start) { const tmp = start; start = end; end = tmp }
      this.rangeStart = start
      this.rangeEnd = end
      this.dateMode = 'range'
      this.activeChip = null
      this.navDate = new Date(start)
      this.closeRangePicker()
      this.page = 1
      this.loadData()
    },
    prevPickerMonth() {
      const d = new Date(this.pickerViewDate)
      d.setMonth(d.getMonth() - 1)
      this.pickerViewDate = d
    },
    nextPickerMonth() {
      const d = new Date(this.pickerViewDate)
      d.setMonth(d.getMonth() + 1)
      this.pickerViewDate = d
    },
    onPickerDayClick(date) {
      if (!this.pickerSelStart || (this.pickerSelStart && this.pickerSelEnd)) {
        // Inicia nueva selección
        this.pickerSelStart = new Date(date)
        this.pickerSelEnd = null
        this.pickerInputDesde = this.toYMD(date)
        this.pickerInputHasta = ''
      } else {
        // Completa la selección
        let end = new Date(date)
        if (end < this.pickerSelStart) {
          const tmp = this.pickerSelStart
          this.pickerSelStart = end
          end = tmp
        }
        this.pickerSelEnd = end
        this.pickerInputDesde = this.toYMD(this.pickerSelStart)
        this.pickerInputHasta = this.toYMD(this.pickerSelEnd)
      }
    },
    isPickerStart(date) {
      if (!this.pickerSelStart) return false
      return this.toYMD(date) === this.toYMD(this.pickerSelStart)
    },
    isPickerEnd(date) {
      if (!this.pickerSelEnd) return false
      return this.toYMD(date) === this.toYMD(this.pickerSelEnd)
    },
    isPickerInRange(date) {
      if (!this.pickerSelStart || !this.pickerSelEnd) return false
      return date > this.pickerSelStart && date < this.pickerSelEnd
    },
    isPickerToday(date) {
      const today = new Date(); today.setHours(0, 0, 0, 0)
      return date.getTime() === today.getTime()
    },
    onPickerDesdeInput(val) {
      this.pickerInputDesde = val
      const d = this.parseYMD(val)
      if (d) {
        this.pickerSelStart = d
        this.pickerViewDate = new Date(d)
      }
    },
    onPickerHastaInput(val) {
      this.pickerInputHasta = val
      const d = this.parseYMD(val)
      if (d) this.pickerSelEnd = d
    },
    setDateChip(chip) {
      const today = new Date()
      this.activeChip = chip
      if (chip === 'today') {
        this.dateMode = 'day'
        this.navDate = new Date(today)
      } else if (chip === 'yesterday') {
        this.dateMode = 'day'
        const y = new Date(today)
        y.setDate(today.getDate() - 1)
        this.navDate = y
      } else if (chip === 'week') {
        this.dateMode = 'week'
        this.navDate = new Date(today)
      } else if (chip === 'month') {
        this.dateMode = 'month'
        this.navDate = new Date(today)
      }
      this.page = 1
      this.loadData()
    },
    navBack() {
      const d = new Date(this.navDate)
      if (this.dateMode === 'month') {
        d.setMonth(d.getMonth() - 1)
        this.activeChip = null
      } else if (this.dateMode === 'week') {
        d.setDate(d.getDate() - 7)
        this.activeChip = null
      } else {
        d.setDate(d.getDate() - 1)
        this.activeChip = this.resolveChip(d)
      }
      this.navDate = d
      this.page = 1
      this.loadData()
    },
    navForward() {
      const d = new Date(this.navDate)
      if (this.dateMode === 'month') {
        d.setMonth(d.getMonth() + 1)
        this.activeChip = null
      } else if (this.dateMode === 'week') {
        d.setDate(d.getDate() + 7)
        this.activeChip = null
      } else {
        d.setDate(d.getDate() + 1)
        this.activeChip = this.resolveChip(d)
      }
      this.navDate = d
      this.page = 1
      this.loadData()
    },

    // ── Helpers de formato ───────────────────────────────────────────────────
    formatHora(hora) {
      if (!hora) return ''
      return hora.substring(0, 5)
    },
    calcPickupTime(hora, minutes) {
      if (!hora || !minutes) return ''
      const [h, m] = hora.split(':').map(Number)
      const total = h * 60 + m + minutes
      const ph = String(Math.floor(total / 60) % 24).padStart(2, '0')
      const pm = String(total % 60).padStart(2, '0')
      return `${ph}:${pm}`
    },
    formatPrecio(precio) {
      if (!precio) return '0.00'
      return parseFloat(precio).toFixed(2)
    },
    normalizeFuente(fuente) {
      if (!fuente) return 'default'
      return fuente.toLowerCase().replace(/[^a-z0-9]/g, '')
    },
    getMonedaLabel(moneda) {
      return moneda === 'DOLARES' ? 'USD' : 'PEN'
    },
    channelLabel(ch) {
      const map = { WHATSAPP: 'WhatsApp', FACEBOOK: 'Facebook', INSTAGRAM: 'Instagram', EMAIL: 'Correo electrónico', PHONE: 'Teléfono', WEB: 'Página web' }
      return map[ch] || ch || '—'
    },
    sourceTypeLabel(st) {
      const map = { DIRECT: 'Directo', OTA: 'OTA', AFFILIATE: 'Afiliado' }
      return map[st] || st || '—'
    },
    getDriverInitials(name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
      return (parts[0][0] + parts[1][0]).toUpperCase()
    },
    getDriverColor(name) {
      const palette = [
        '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b',
        '#10b981', '#ef4444', '#06b6d4', '#f97316',
        '#6366f1', '#14b8a6'
      ]
      if (!name) return palette[0]
      let hash = 0
      for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
      return palette[Math.abs(hash) % palette.length]
    },
    getEstadoColor(estado) {
      return (this.statusMeta[estado] || {}).badge || 'badge-secondary'
    },
    getPagoColor(estadoPago) {
      const colors = {
        'Pendiente': 'badge-secondary',
        'Pagado': 'badge-success',
        'Cancelado': 'badge-danger'
      }
      return colors[estadoPago] || 'badge-secondary'
    },

    // ── Filtro por card ───────────────────────────────────────────────────────
    setCardFilter(card, field, value) {
      this.activeCard = card
      this.cardFilter = { field, value }
      this.page = 1
      this.loadData()
    },

    // ── Drawer ───────────────────────────────────────────────────────────────
    async openDrawer(id) {
      try {
        await this.$store.dispatch('app/loading', true)
        const response = await RideService.getById(id)
        this.drawerRide = response.payload
        this.drawerOpen = true
        document.body.style.overflow = 'hidden'
      } catch (error) {
        console.error(error)
        Alert.error('No se pudo cargar el detalle del viaje')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    closeDrawer() {
      this.drawerOpen = false
      document.body.style.overflow = ''
    },

    // ── Acciones de tabla ────────────────────────────────────────────────────
    onEditar(ride) {
      this.$router.push({ name: 'viajes-ride-edit', params: { id: ride.id } })
    },
    onEliminar(ride) {
      this.selectedOption = ride
      this.openConfirmationModal({
        title: 'Eliminar viaje',
        description: `¿Está seguro que desea eliminar el viaje ${ride.ride}?`,
        acceptText: 'Eliminar',
        action: 'delete'
      })
    },
    confirmModal() {
      this.closeConfirmationModal()
      if (this.confirmationModal.action === 'delete') this.deleteItem()
    },
    async deleteItem() {
      try {
        await this.$store.dispatch('app/loading', true)
        await RideService.delete(this.selectedOption.id)
        await this.loadData()
        Alert.success('Viaje eliminado correctamente')
      } catch (ex) {
        if (ex && (ex.status === 200 || ex.status === 204)) {
          await this.loadData()
          Alert.success('Viaje eliminado correctamente')
          return
        }
        console.error(ex)
        Alert.error(ex?.errors?.message || 'Error al eliminar el viaje')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    // ── Cambio de estado ─────────────────────────────────────────────────────
    isTerminalEstado(estado) {
      return (this.statusTransitions[estado] || []).length === 0
    },
    openStatusDropdown(item, event) {
      const transitions = this.statusTransitions[item.estado] || []
      if (transitions.length === 0) return
      const rect = event.currentTarget.getBoundingClientRect()
      const options = transitions.map(key => ({ key, ...this.statusMeta[key] }))
      const left = Math.min(rect.left, window.innerWidth - 180)
      this.statusDropdown = { show: true, ride: item, options, top: rect.bottom + 4, left }
      setTimeout(() => document.addEventListener('click', this._statusDropdownHandler), 0)
    },
    closeStatusDropdown() {
      this.statusDropdown.show = false
      document.removeEventListener('click', this._statusDropdownHandler)
    },
    async submitStatus(newEstado) {
      const rideId = this.statusDropdown.ride.id
      this.closeStatusDropdown()
      try {
        await this.$store.dispatch('app/loading', true)
        await RideService.updateStatus(rideId, newEstado)
        await this.loadData()
        Alert.success('Estado actualizado correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex?.errors?.message || 'Error al cambiar el estado')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    // ── Pago ─────────────────────────────────────────────────────────────────
    openPaymentModal(item) {
      this.paymentModal.ride = item
      this.paymentModal.estado_pago = item.estado_pago || ''
      this.paymentModal.medio_pago = item.medio_pago || ''
      this.paymentModal.fecha_pago = item.fecha_pago || ''
      this.paymentModal.hora_pago = item.hora_pago ? item.hora_pago.substring(0, 5) : ''
      this.paymentModal.num_operacion = item.num_operacion || ''
      this.paymentModal.comprobante = item.comprobante || ''
      this.paymentModal.show = true
    },
    closePaymentModal() {
      this.paymentModal.show = false
    },
    async submitPayment() {
      if (!this.paymentModal.estado_pago) {
        Alert.error('El estado de pago es obligatorio')
        return
      }
      try {
        await this.$store.dispatch('app/loading', true)
        const payload = {
          estado_pago: this.paymentModal.estado_pago,
          medio_pago: this.paymentModal.medio_pago || null,
          fecha_pago: this.paymentModal.fecha_pago || null,
          hora_pago: this.paymentModal.hora_pago || null,
          num_operacion: this.paymentModal.num_operacion || null,
          comprobante: this.paymentModal.comprobante || null
        }
        await RideService.updatePayment(this.paymentModal.ride.id, payload)
        this.closePaymentModal()
        await this.loadData()
        Alert.success('Pago registrado correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex?.errors?.message || 'Error al registrar el pago')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    // ── Asignación ───────────────────────────────────────────────────────────
    async openAssignModal(item) {
      this.assignModal.ride = item
      this.assignModal.conductor_id = item.conductor_id || null
      this.assignModal.vehiculo_id = item.vehiculo_id || null
      if (!this.driverOptions.length) await this.loadDriverOptions()
      if (!this.vehicleOptions.length) await this.loadVehicleOptions()
      this.assignModal.show = true
    },
    closeAssignModal() {
      this.assignModal.show = false
    },
    async loadDriverOptions() {
      try {
        const response = await DriverService.list({ status: 1 }, { page: 0, size: 200 })
        this.driverOptions = response.payload.items
      } catch (error) {
        console.error('Error cargando conductores:', error)
      }
    },
    async loadVehicleOptions() {
      try {
        const response = await VehicleService.list({ status: 1 }, { page: 0, size: 200 })
        this.vehicleOptions = response.payload.items
      } catch (error) {
        console.error('Error cargando vehículos:', error)
      }
    },
    async submitAssign() {
      try {
        await this.$store.dispatch('app/loading', true)
        await RideService.assign(this.assignModal.ride.id, {
          conductor_id: this.assignModal.conductor_id,
          vehiculo_id: this.assignModal.vehiculo_id
        })
        this.closeAssignModal()
        await this.loadData()
        Alert.success('Asignación actualizada correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex?.errors?.message || 'Error al guardar la asignación')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async clearAssign() {
      try {
        await this.$store.dispatch('app/loading', true)
        await RideService.assign(this.assignModal.ride.id, {
          conductor_id: null,
          vehiculo_id: null
        })
        this.closeAssignModal()
        await this.loadData()
        Alert.success('Asignación eliminada correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex?.errors?.message || 'Error al quitar la asignación')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    // ── Carga de datos ───────────────────────────────────────────────────────
    async exportExcel() {
      if (this.exporting) return
      this.exporting = true
      try {
        const { desde, hasta } = this.getDateRange()
        const dateFilter = {}
        if (desde) dateFilter.fecha_desde = desde
        if (hasta) dateFilter.fecha_hasta = hasta
        const activeFilters = Object.fromEntries(
          Object.entries(this.inlineFilters).filter(([, v]) => v !== '')
        )
        const cardExtra = this.cardFilter.field
          ? { [this.cardFilter.field]: this.cardFilter.value }
          : {}
        const data = { search: this.search, ...activeFilters, ...cardExtra, ...dateFilter }
        const response = await RideService.exportExcel(data)
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `Viajes_Travelcab_${new Date().toISOString().slice(0, 10)}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (ex) {
        console.error(ex)
        Alert.error('Error al exportar los viajes')
      } finally {
        this.exporting = false
      }
    },
    async loadBookingSources() {
      try {
        const response = await RideService.getBookingSources()
        this.bookingSources = response.payload || { fixed: [], otas: [], affiliates: [] }
      } catch {
        this.bookingSources = { fixed: [], otas: [], affiliates: [] }
      }
    },
    async loadStats() {
      try {
        const { desde, hasta } = this.getDateRange()
        const params = {}
        if (desde) params.fecha_desde = desde
        if (hasta) params.fecha_hasta = hasta
        const response = await RideService.stats(params)
        this.stats = response.payload || {}
      } catch (error) {
        console.error('Error cargando estadísticas:', error)
      }
    },
    // ── Filtros inline ───────────────────────────────────────────────────────
    onFilterChange() {
      this.page = 1
      this.loadData()
    },
    clearInlineFilters() {
      this.inlineFilters = { fuente: '', estado_pago: '', moneda: '', tipo_viaje: '' }
      this.page = 1
      this.loadData()
    },

    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const params = { page: this.page - 1, size: this.pageSize }
        const cardExtra = this.cardFilter.field
          ? { [this.cardFilter.field]: this.cardFilter.value }
          : {}
        const { desde, hasta } = this.getDateRange()
        const dateFilter = {}
        if (desde) dateFilter.fecha_desde = desde
        if (hasta) dateFilter.fecha_hasta = hasta
        const activeFilters = Object.fromEntries(
          Object.entries(this.inlineFilters).filter(([, v]) => v !== '')
        )
        const data = {
          search: this.search,
          ...activeFilters,
          ...cardExtra,
          ...dateFilter
        }
        const [response] = await Promise.all([
          RideService.list(data, params),
          this.loadStats()
        ])
        this.list = response.payload.items
        this.total = response.payload.metadata.totalElements
        this.pages = response.payload.metadata.totalPages
      } catch (error) {
        console.error(error)
        Alert.error(error)
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    }
  },
  created() {
    this._statusDropdownHandler = () => this.closeStatusDropdown()
    this._rangePickerHandler = () => this.closeRangePicker()
  },
  async mounted() {
    await Promise.all([this.loadData(), this.loadBookingSources()])
  },
  beforeDestroy() {
    document.body.style.overflow = ''
    document.removeEventListener('click', this._statusDropdownHandler)
    document.removeEventListener('click', this._rangePickerHandler)
  }
}
</script>

<style lang="stylus" scoped>
// ── Tabla ─────────────────────────────────────────────────────────────────────
.table-scroll
  overflow-x auto

.ride-code
  font-size 0.8rem
  white-space nowrap

.clickable-row
  cursor pointer
  &:hover
    background-color #f8fafc

// Anchos de columna
.col-ride
  min-width 90px
.col-fecha
  min-width 95px
.col-fuente
  min-width 90px
.col-ruta
  min-width 180px
  max-width 220px
.col-tipo
  min-width 100px
.col-pasajero
  min-width 150px
.col-pax
  min-width 80px
.col-vehiculo
  min-width 90px
.col-conductor
  min-width 160px
  max-width 200px
.col-estado
  min-width 100px
.col-pago
  min-width 100px
.col-precio
  min-width 90px

.price-original
  text-decoration line-through
  color #9ca3af
  font-size 0.8em
.col-opciones
  min-width 70px

// Ruta
.ruta-item
  display flex
  align-items center
  gap 6px

.ruta-dot
  flex-shrink 0
  width 8px
  height 8px
  border-radius 50%
  &--origen
    background-color #3b82f6
  &--destino
    background-color #f97316

.ruta-text
  max-width 180px
  font-size 0.82rem

// Conductor
.conductor-cell
  display flex
  align-items center
  gap 8px

.conductor-cell--clickable
  cursor pointer
  &:hover .conductor-info__name
    color #3b82f6

.conductor-avatar
  flex-shrink 0
  width 32px
  height 32px
  border-radius 50%
  display flex
  align-items center
  justify-content center
  font-size 0.72rem
  font-weight 700
  color #fff
  letter-spacing 0.5px

.conductor-info
  display flex
  flex-direction column
  min-width 0

.conductor-info__name
  font-size 0.82rem
  font-weight 600
  color #1f2937
  line-height 1.2

.conductor-info__plate
  font-size 0.72rem
  color #9ca3af
  margin-top 1px
  line-height 1.2

.btn-asignar
  font-size 0.75rem
  padding 3px 10px
  border 1.5px dashed #9ca3af
  border-radius 6px
  background transparent
  color #9ca3af
  cursor pointer
  white-space nowrap
  transition border-color 0.15s, color 0.15s
  &:hover
    border-color #3b82f6
    color #3b82f6

.assign-label
  font-size 0.82rem
  font-weight 600
  color #374151
  margin-bottom 4px

// Pax
.pax-info
  font-size 0.85rem
  white-space nowrap

// Badges fuente
.badge-fuente
  font-size 0.72rem
  padding 3px 7px
  border-radius 4px
  color #fff
  font-weight 600
  &--elife
    background-color #7c3aed
  &--viator
    background-color #0ea5e9
  &--ff
    background-color #d97706
  &--polittan
    background-color #059669
  &--web
    background-color #0369a1
    color #ffffff
  &--default
    background-color #6b7280

// Badge vehículo
.badge-vehicle
  background-color #e5e7eb
  color #374151
  font-size 0.72rem
  padding 3px 7px

// Badge moneda
.badge-moneda
  font-size 0.68rem
  padding 2px 5px
  border-radius 3px
  font-weight 700
  &--DOLARES
    background-color #d1fae5
    color #065f46
  &--SOLES
    background-color #dbeafe
    color #1e40af

// Badge de estado clicable
.badge-estado-clickable
  cursor pointer
  transition filter 0.15s
  &:hover
    filter brightness(0.88)

// Dropdown de estado
.status-dropdown
  position fixed
  z-index 2000
  background-color #fff
  border 1px solid #e5e7eb
  border-radius 8px
  box-shadow 0 8px 24px rgba(0,0,0,0.12)
  min-width 160px
  padding 4px 0
  overflow hidden

.status-dropdown__header
  font-size 0.65rem
  font-weight 700
  text-transform uppercase
  letter-spacing 0.6px
  color #9ca3af
  padding 8px 12px 4px

.status-dropdown__empty
  font-size 0.8rem
  color #9ca3af
  padding 8px 12px
  display flex
  align-items center
  gap 6px

.status-dropdown__item
  display flex
  align-items center
  width 100%
  padding 7px 12px
  background transparent
  border none
  cursor pointer
  text-align left
  transition background-color 0.1s
  &:hover
    background-color #f3f4f6

// Badge de pago clicable
>>> .badge-pago-clickable
  cursor pointer
  transition filter 0.15s
  &:hover
    filter brightness(0.88)

// badge-orange sin scope para clase dinámica
>>> .badge-orange
  background-color #f97316
  color #fff

>>> .badge-warning
  background-color #f59e0b
  color #fff

>>> .badge-purple
  background-color #7c3aed
  color #fff



// ── Toolbar ───────────────────────────────────────────────────────────────────
.toolbar-row
  display flex
  align-items center
  justify-content space-between
  flex-wrap wrap
  gap 8px
  margin-bottom 4px

.toolbar-filters
  display flex
  align-items center
  flex-wrap wrap
  gap 6px

.toolbar-select
  height 34px
  padding 0 28px 0 10px
  font-size 0.8rem
  border 1px solid #d1d5db
  border-radius 6px
  background-color #fff
  color #374151
  appearance none
  background-image url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%236b7280' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")
  background-repeat no-repeat
  background-position right 8px center
  cursor pointer
  &:focus
    outline none
    border-color #3b82f6

.toolbar-clear-icon
  width 28px
  height 28px
  padding 0
  font-size 14px
  line-height 28px
  text-align center
  border none
  border-radius 6px
  background-color #fef2f2
  color #ef4444
  cursor pointer
  display inline-flex
  align-items center
  justify-content center
  &:hover
    background-color #fee2e2
    color #dc2626

.btn-export
  font-size 0.82rem
  i
    margin-right 3px

.toolbar-right
  display flex
  align-items center
  gap 8px

// ── Cards de estadísticas ─────────────────────────────────────────────────────
.stats-row
  display flex
  flex-wrap nowrap
  justify-content flex-start
  gap 8px
  overflow-x auto
  padding 4px 4px 4px 4px
  margin-bottom 16px
  -webkit-overflow-scrolling touch

.stat-card
  flex-shrink 0
  min-width 100px
  border-radius 10px
  padding 10px 14px
  display flex
  flex-direction column
  gap 2px
  background #fff
  border 1.5px solid #e9ecef
  cursor pointer
  transition box-shadow 0.15s, border-color 0.15s
  &:hover
    box-shadow 0 2px 8px rgba(0,0,0,0.07)
    border-color #c8d0dc
  &.is-active
    border-color currentColor
    box-shadow 0 0 0 1px currentColor

.stat-card__label
  display flex
  align-items center
  gap 5px
  font-size 0.6rem
  font-weight 700
  text-transform uppercase
  letter-spacing 0.5px
  color #6b7280

.stat-card__dot
  display inline-block
  width 7px
  height 7px
  border-radius 50%
  flex-shrink 0

.stat-card__icon
  font-size 0.7rem
  line-height 1
  flex-shrink 0

.stat-card__value
  font-size 1.5rem
  font-weight 800
  line-height 1.1

.stat-card__sub
  font-size 0.62rem
  color #9ca3af

// Variantes de color
.stat-card--total
  background #eff6ff
  border-color #bfdbfe
  color #1d4ed8
  .stat-card__label
    color #1d4ed8

.stat-card--pendiente
  color #d97706
  .stat-card__dot
    background #f59e0b

.stat-card--asignado
  color #2563eb
  .stat-card__dot
    background #3b82f6

.stat-card--ubicado
  color #0891b2
  .stat-card__dot
    background #06b6d4

.stat-card--finalizado
  color #059669
  .stat-card__dot
    background #10b981

.stat-card--cancelado
  color #dc2626
  .stat-card__dot
    background #ef4444

.stat-card--enproceso
  color #d97706
  .stat-card__dot
    background #f59e0b

.stat-card--nonshow
  color #dc2626
  .stat-card__dot
    background #ef4444

.stat-card--error
  color #991b1b
  .stat-card__dot
    background #b91c1c

.stat-card--pendcobro
  color #b45309
  background #fffbeb
  border-color #fde68a
  .stat-card__icon
    color #d97706


// ── Navegador de fechas ───────────────────────────────────────────────────────
.date-nav-row
  display flex
  align-items center
  justify-content center
  flex-wrap wrap
  gap 8px

.date-nav
  display flex
  align-items center
  gap 6px
  flex-wrap wrap

.date-nav__arrow
  display flex
  align-items center
  justify-content center
  width 30px
  height 30px
  border-radius 8px
  border 1px solid #e5e7eb
  background-color #fff
  cursor pointer
  color #374151
  font-size 1.1rem
  transition background-color 0.15s, border-color 0.15s
  &:hover
    background-color #f3f4f6
    border-color #d1d5db

.date-nav__label-wrap
  position relative

.date-nav__label-btn
  padding 5px 14px
  height 30px
  border-radius 6px
  border 1px solid #e5e7eb
  background-color #fff
  cursor pointer
  color #111827
  font-size 0.82rem
  font-weight 600
  white-space nowrap
  display flex
  align-items center
  transition background-color 0.15s, border-color 0.15s
  &:hover
    background-color #f0f7ff
    border-color #93c5fd

.date-nav__sep
  width 1px
  height 20px
  background-color #e5e7eb
  margin 0 4px

.date-nav__chips
  display flex
  align-items center
  gap 4px

.date-nav__chip
  padding 4px 10px
  border-radius 8px
  border 1px solid #e5e7eb
  background-color #fff
  cursor pointer
  color #6b7280
  font-size 0.78rem
  font-weight 500
  transition background-color 0.15s, color 0.15s, border-color 0.15s
  white-space nowrap
  &:hover
    background-color #f3f4f6
    border-color #d1d5db
  &.is-active
    background-color #365EFF
    border-color #365EFF
    color #fff

// ── Date range picker ────────────────────────────────────────────────────────
.date-range-picker
  position absolute
  top calc(100% + 6px)
  left 0
  z-index 2100
  background-color #fff
  border 1px solid #e5e7eb
  border-radius 10px
  box-shadow 0 12px 32px rgba(0,0,0,0.14)
  width 280px
  padding 12px

.drp-header
  display flex
  align-items center
  justify-content space-between
  margin-bottom 10px

.drp-nav
  display flex
  align-items center
  justify-content center
  width 28px
  height 28px
  border-radius 6px
  border 1px solid #e5e7eb
  background-color #fff
  cursor pointer
  color #374151
  font-size 1rem
  transition background-color 0.12s
  &:hover
    background-color #f3f4f6

.drp-month-label
  font-size 0.85rem
  font-weight 700
  color #111827

.drp-weekdays
  display grid
  grid-template-columns repeat(7, 1fr)
  margin-bottom 4px

.drp-weekday
  text-align center
  font-size 0.65rem
  font-weight 600
  text-transform uppercase
  letter-spacing 0.4px
  color #9ca3af
  padding 2px 0

.drp-grid
  display grid
  grid-template-columns repeat(7, 1fr)
  gap 1px

.drp-day
  position relative
  display flex
  align-items center
  justify-content center
  height 32px
  border none
  background transparent
  cursor pointer
  font-size 0.8rem
  color #374151
  border-radius 0
  transition background-color 0.1s, color 0.1s
  &:hover:not(.drp-day--empty):not(.drp-day--start):not(.drp-day--end)
    background-color #dbeafe
    color #1e40af
  &--empty
    cursor default
    pointer-events none
  &--today
    color #2563eb
    font-weight 700
    background-color #eff6ff
  &--range
    background-color #dbeafe
    color #1e40af
    border-radius 0
  &--start
    background-color #2563eb
    color #fff
    border-radius 6px 0 0 6px
    font-weight 600
  &--end
    background-color #2563eb
    color #fff
    border-radius 0 6px 6px 0
    font-weight 600
  &--start&--end
    border-radius 6px

.drp-inputs
  display flex
  align-items flex-end
  gap 6px
  margin-top 12px
  padding-top 10px
  border-top 1px solid #f3f4f6

.drp-input-group
  flex 1
  display flex
  flex-direction column
  gap 3px

.drp-input-label
  font-size 0.68rem
  font-weight 600
  color #6b7280
  text-transform uppercase
  letter-spacing 0.4px

.drp-input-sep
  font-size 0.9rem
  color #9ca3af
  padding-bottom 6px
  flex-shrink 0

.drp-footer
  display flex
  justify-content flex-end
  gap 8px
  margin-top 10px
  padding-top 10px
  border-top 1px solid #f3f4f6

// ── Overlay ───────────────────────────────────────────────────────────────────
.ride-overlay
  position fixed
  inset 0
  background-color rgba(0,0,0,0.4)
  z-index 1040

// ── Drawer ────────────────────────────────────────────────────────────────────
.ride-drawer
  position fixed
  top 0
  right 0
  width 420px
  max-width 95vw
  height 100vh
  background-color #fff
  z-index 1050
  display flex
  flex-direction column
  box-shadow -4px 0 24px rgba(0,0,0,0.15)

.drawer-header
  display flex
  align-items flex-start
  justify-content space-between
  padding 20px 20px 16px
  border-bottom 1px solid #e5e7eb
  flex-shrink 0

.drawer-header__main
  display flex
  flex-direction column
  gap 6px

.drawer-ride-code
  font-size 1.25rem
  font-weight 700
  color #111827
  letter-spacing 0.5px

.drawer-ride-datetime
  font-size 0.85rem
  color #6b7280

.drawer-close
  background none
  border none
  cursor pointer
  font-size 1.4rem
  color #6b7280
  line-height 1
  padding 0
  &:hover
    color #111827

.drawer-body
  flex 1
  overflow-y auto
  padding 0 20px 20px

.drawer-section
  padding 16px 0
  border-bottom 1px solid #f3f4f6
  &:last-child
    border-bottom none

.drawer-section__title
  font-size 0.7rem
  font-weight 700
  text-transform uppercase
  letter-spacing 0.8px
  color #9ca3af
  margin-bottom 12px

.drawer-row
  display flex
  align-items flex-start
  justify-content space-between
  gap 12px
  margin-bottom 8px
  &:last-child
    margin-bottom 0

.drawer-label
  font-size 0.82rem
  color #6b7280
  white-space nowrap
  flex-shrink 0
  min-width 120px

.drawer-value
  font-size 0.85rem
  color #111827
  text-align right

// Timeline ruta
.drawer-timeline
  display flex
  gap 12px
  align-items stretch

.drawer-timeline__row
  display flex
  flex-direction column
  align-items center
  padding-top 4px

.drawer-timeline__dot
  width 10px
  height 10px
  border-radius 50%
  flex-shrink 0
  &--origen
    background-color #3b82f6
  &--destino
    background-color #f97316

.drawer-timeline__line
  flex 1
  width 2px
  background-color #e5e7eb
  margin 4px 0

.drawer-timeline__labels
  flex 1
  display flex
  flex-direction column
  gap 8px

.drawer-timeline__label
  font-size 0.85rem
  color #111827
  line-height 1.4

.drawer-footer
  padding 16px 20px
  border-top 1px solid #e5e7eb
  flex-shrink 0
  display flex
  align-items center

// ── Transiciones ──────────────────────────────────────────────────────────────
.fade-enter-active,
.fade-leave-active
  transition opacity 0.25s ease

.fade-enter,
.fade-leave-to
  opacity 0

.slide-drawer-enter-active,
.slide-drawer-leave-active
  transition transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)

.slide-drawer-enter,
.slide-drawer-leave-to
  transform translateX(100%)
</style>
